/**
 * Minimal typed mirror of the GFE category endpoint (`GET /api/gfe/v1/categories/:categoryUid`).
 * Only the fields the web client reads are declared.
 * Source of truth: gaming-fusion-engine `src/types/{category,page,game,common}.types.ts`.
 */

import type { NavigationItemLinkType, RegionInfo } from '@/types/aggregation/common';
import type { Experiment } from '@/types/aggregation/home';
import type { EpicHeaderLabel } from '@/types/aggregation/category-v2';
import type { RichTextElement } from '@/app/components/rich-text/rich-text.types';
import type { PRODUCT_THEME } from '@/utils/constants';
import type { GfeHeaderConfig, GfeFooterConfig } from '@/types/gfe/chrome';

/**
 * Flattened Site-Wide Jackpot (SWJ) contract. Source of truth: GCM
 * `lambda/shared/types/jackpot.ts` (mirrored in GFE `src/types/jackpot.types.ts`).
 * GCM flattens the Prismic `jackpot` doc, GFE serves it on the category response
 * at `.siteWideJackpot`, and the DYB category header renders it directly.
 */

/** One inline formatting span within a Prismic rich-text field (bold, link, etc.). */
export type SwjRichTextSpan = {
    start: number;
    end: number;
    type: string;
};

/**
 * First-line rich text preserved with its inline spans. The DYB opt-in copy
 * (`extraInfo`) carries inline formatting the opt-in popup renders, so the spans
 * must survive the flatten.
 */
export type SwjRichText = {
    text: string;
    spans: SwjRichTextSpan[];
};

/** One `jackpot_multiplier_boost` (Double-Your-Bet) item. */
export type SwjMultiplier = {
    /** 0 = opted out, 1 = single bet, 2 = double bet. */
    multiplier: number;
    wageredAmount: number;
    shortWagerLabel: string;
    wagerLabel: string;
    userState: string;
    oneLinerTitle: string;
    extraInfo: SwjRichText;
};

/** One jackpot tier — consumed by the ticker / cards / animated top bar. */
export type SwjTier = {
    tierId: string;
    tierName: string;
    shortTierName: string;
    logo1: string;
    minSeed: number;
    odds: number;
};

/**
 * The full flattened Site-Wide Jackpot blob. `null` on the category response when
 * no SWJ zone is authored for the product.
 */
export type FlattenedSiteWideJackpot = {
    enabled: boolean;
    name: string;
    providerJackpotId: string;
    providerUid: string;
    oneLiner: string;
    introTitle: string;
    subHeadline: string;
    logo: string;
    animatedLogo: string;
    alternateLogo: string;
    rulesPath: string;
    openRulesExternally: boolean;
    dybShortRules: string;
    multipliers: SwjMultiplier[];
    collapsedOptedOutText: string;
    tiers: SwjTier[];
};

/** Slim game summary, resolved from {@link GfeGamesIndex} by uid. */
export type GfeGameSummary = {
    uid: string;
    name: string;
    description: string;
    logo: string;
    background: string;
    label: string | null;
    isSiteWideJackpot: boolean;
    /** Effective status for the requested platform (e.g. `Active`, `Disabled`). */
    status: string;
    /** Trailer video URL, present on games authored with one (consumed by `animated_trailer` zones). */
    video_trailer?: string;
    /** Social tag derived from the AZC ranking: `'hot'` (top 5%) or `'cold'`. Absent when unranked. */
    badgeState?: 'hot' | 'cold';
    /** Social tag "playing now" count from the AZC ranking. Absent when unranked/unavailable. */
    uniquePlayerCount?: number;
    /** Gameplay trailer URL. Present only for the game(s) featured in a hero header zone (e.g. `animated_trailer`). */
    gamePlayTrailer?: string | null;
    /** Full-screen gameplay trailer URL. Present only for games in an `animated_trailer` header zone (Explore videos). */
    fullscreenGamePlayTrailer?: string | null;
    /** Merged/flattened tile image (largest variant). Present only for games in an `animated_trailer` header zone (Explore videos); used as the game tile below the video. */
    flattened?: string | null;
};

/** Deduplicated game summaries keyed by uid. */
export type GfeGamesIndex = Record<string, GfeGameSummary>;

export type GfeZoneLayout = 'Default' | 'Spotlight' | '1 Row' | '2 Row' | 'Tall' | 'Vertical Featured Grid' | null;

/** Above-fold zones are hydrated (`gameUids` present); below-fold are skeletons. */
export type GfeAssembledZone = {
    uid: string;
    /** The CMS `slice_type` verbatim (e.g. `'collection'`, `'trending_games_configuration'`). */
    type: string;
    title: string;
    layout: GfeZoneLayout;
    hydrated: boolean;
    gameUids?: string[];
    totalGames?: number;
    config?: Record<string, unknown>;
    /** Present on `influencer_gameplay_sessions` zones instead of `gameUids`. */
    gameplaySessions?: GfeGameplaySession[];
};

/**
 * Category header/hero zone. GFE (via GCM) flattens the CMS `category_header` slice to
 * these display fields, so the web adapter forwards them straight into the renderer's
 * `ProcessedCategoryHeaderSlicePrimary` — no per-field processing needed. Field names
 * mirror the CMS slice (`header_image_desktop` / `header_image_mobile`); native/iOS maps
 * them to its own `large_image` / `small_image`.
 */
export type GfeCategoryHeaderConfig = {
    title?: string | null;
    subtitle?: string | null;
    desktopImageUrl?: string | null;
    mobileImageUrl?: string | null;
};

export type GfeCategoryHeaderZone = GfeAssembledZone & {
    config?: GfeCategoryHeaderConfig;
};

/**
 * One orientation slice of an Epic Hub (exclusive-launch) header. Field names mirror the CMS
 * slice so the web adapter can forward them straight into `CategoryV2HeaderSlice`.
 */
export type GfeHeaderSlice = {
    slice_type: string;
    cta_button_text?: string;
    announcement_background_image_url?: string;
    background_image_url?: string;
    logo_image_url?: string;
};

/**
 * Linked-game reference carried on hub headers / gameplay sessions. Only the display fields the web
 * renders. Per-context needs: Epic Hub uses `uid`/`display_name`/`label`/`copyright_text` (art comes
 * from the header slice, not here); Gameplay Session + influencer zone tiles use `background`/`logo`.
 * `id` is optional and unused today (the launcher short-circuits to the `uid` path).
 */
export type GfeLinkedGame = {
    id?: string;
    uid?: string;
    display_name?: string;
    label?: string;
    copyright_text?: string | null;
    background?: string;
    logo?: string;
};

/** Influencer profile carried on gameplay-session headers / zone items. */
export type GfeInfluencerProfile = {
    uid?: string;
    name?: string;
    avatar_url?: string;
    background_url?: string;
};

/** Epic Hub header payload (`exclusive_launch_zone`). Mirrors aggregation `CategoryV2EpicHubHeaderPayload`. */
export type GfeEpicHubHeaderPayload = {
    uid?: string;
    description?: string;
    launch_date?: string;
    epic_header_label?: EpicHeaderLabel;
    portrait?: GfeHeaderSlice[];
    landscape?: GfeHeaderSlice[];
    linked_game?: GfeLinkedGame;
};

/** Gameplay Session header payload (`gameplay_session`). Mirrors aggregation `CategoryV2GameplaySessionHeaderPayload`. */
export type GfeGameplaySessionHeaderPayload = {
    uid?: string;
    title?: string;
    gameplay_video_url?: string;
    video_placeholder_url?: string;
    linked_game?: GfeLinkedGame;
    linked_profile?: GfeInfluencerProfile;
};

/**
 * Hub header discriminant, mirroring aggregation `header_category.type`. The `category_header`
 * branch reuses the flattened {@link GfeCategoryHeaderConfig} hero fields.
 */
export type GfeHubHeaderCategory =
    | { type: 'exclusive_launch_zone'; headerPayload: GfeEpicHubHeaderPayload }
    | { type: 'gameplay_session'; headerPayload: GfeGameplaySessionHeaderPayload }
    | { type: 'category_header'; data: GfeCategoryHeaderConfig };

/** One influencer gameplay-session content item carried on an `influencer_gameplay_sessions` zone. */
export type GfeGameplaySession = {
    uid?: string;
    title?: string;
    gameplay_video_url?: string;
    video_placeholder?: string;
    thumbnail?: string;
    game?: GfeLinkedGame;
    profile?: GfeInfluencerProfile;
    gameplay_session_page?: { uid?: string };
};

export type GfeNavItem = {
    uid: string;
    name: string;
    link: string;
    linkType: NavigationItemLinkType;
    icon: string | null;
    customImage: string | null;
    customImageActive: string | null;
    itemType: string | null;
    pinned?: boolean;
    amplitudeExperimentId: string | null;
    amplitudeVariantId: string | null;
};

export type GfeNavigationData = {
    items: GfeNavItem[];
    personalized: boolean;
};

export type GfeFilterFacet = {
    uid: string;
    displayName: string;
    count: number;
};

export type GfeCategoryFilters = {
    providers: GfeFilterFacet[];
    themes: GfeFilterFacet[];
    genres: GfeFilterFacet[];
    features: GfeFilterFacet[];
};

export type GfeGameFilterMetadata = {
    provider: string;
    themes: string[];
    genres: string[];
    features: string[];
};

export type GfeGameFiltersIndex = Record<string, GfeGameFilterMetadata>;

export type GfeCategorySeo = {
    metaTitle: string | null;
    metaDescription: string | null;
    descriptionHeadline: string | null;
    description: RichTextElement[] | null;
};

/**
 * Native-app "Download the app" banner data. GFE serves this on the category
 * response ONLY for `platform=mobile-web` (the Android browser — the sole client
 * that renders the banner); `null`/absent on every other platform. Consumed by
 * the Android {@link SmartBanner}.
 */
export type GfeAppContent = {
    title: string;
    author: string;
    price: string;
    iconUrl: string | null;
    playStoreLink: string;
    openLinkAndroid: string;
};

export type GfeCategoryMeta = {
    categoryUid: string;
    displayName: string;
    webTitle: string;
    categoryType: string;
    totalGames: number;
    totalFilteredGames: number;
    offset: number;
    limit: number;
    hasMore: boolean;
    sort: string;
    activeFilters: Record<string, string[]>;
    requestId: string;
    snapshotVersion: string;
};

export type GfeCategoryResponse = {
    meta: GfeCategoryMeta;
    navigation?: GfeNavigationData;
    bottomBar?: GfeNavItem[] | null;
    zones: GfeAssembledZone[];
    headerZone?: GfeCategoryHeaderZone;
    headerCategory?: GfeHubHeaderCategory;
    gamesIndex: GfeGamesIndex;
    filters?: GfeCategoryFilters;
    gameFiltersIndex?: GfeGameFiltersIndex;
    siteWideJackpot?: FlattenedSiteWideJackpot | null;
    header?: GfeHeaderConfig;
    footer?: GfeFooterConfig;
    seo?: GfeCategorySeo;
    features?: string[];
    experiments?: Experiment;
    productTheme?: PRODUCT_THEME;
    app?: GfeAppContent | null;
};

/**
 * What GFE returns instead of a category, with HTTP 200, when the region is in maintenance or
 * coming soon. It carries nothing else; an active region's normal body never carries `regionInfo`.
 */
export type GfeCategoryMaintenanceResponse = { regionInfo: RegionInfo };

export const isGfeMaintenanceResponse = (
    response: GfeCategoryResponse | GfeCategoryMaintenanceResponse
): response is GfeCategoryMaintenanceResponse => 'regionInfo' in response;

export type GfeSortMode = 'relevance' | 'az' | 'za';

/** Request options for the category endpoint. Mirrors the GFE query schema. */
export type GfeCategoryRequestOptions = {
    offset?: number;
    limit?: number;
    sort?: GfeSortMode;
    provider?: string[];
    theme?: string[];
    genre?: string[];
    feature?: string[];
    aboveFold?: number | 'all';
    includeProduct?: boolean;
};
